/**
 * TaskDetail — single task surface.
 * Actions: Complete / Reassign / Add Note.
 * Role-gated reassignment (Supervisor+). Emits SessionEvents on mutations.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, CheckCircle2, UserCheck, StickyNote } from '@retail-ops/panel-shell/fi';
import { Banner, Button, Pill, WidgetCard, KVPair } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import type { TaskRecordDto } from '@retail-ops/shared-types';
import type { UserRole } from '@retail-ops/shared-types/enums';
import { ReassignModal } from './ReassignModal';
import { formatLocal } from '@/utils/time';
import './rtm.css';

const SUPERVISORY_ROLES: ReadonlyArray<UserRole> = [
  'Supervisor',
  'Manager',
  'Executive',
  'SuperAdmin',
];

export function TaskDetail(): JSX.Element {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { user } = useAuth();

  const [task, setTask] = useState<TaskRecordDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string>('');
  const [resolution, setResolution] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [showReassign, setShowReassign] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!taskId) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    void (async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const resp = await apiClient.retail.tasks.get(taskId, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        if (resp.success && resp.task) setTask(resp.task);
        else setError(resp.error ?? 'Task not found.');
      } catch (err) {
        if (!ctrl.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load task.');
        }
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [apiClient, taskId]);

  const canReassign = user?.primaryRole && SUPERVISORY_ROLES.includes(user.primaryRole);

  const complete = async (): Promise<void> => {
    if (!task || !user) return;
    setBusy(true);
    setNotice(null);
    try {
      await apiClient.retail.tasks.complete(task.taskId, {
        completedBy: user.displayName,
        completedByEmail: user.upn,
        resolution: resolution.trim() || 'Completed.',
        ...(note.trim() ? { notes: note.trim() } : {}),
      });
      await apiClient.retail.events.write({
        eventType: 'task-completed',
        eventCategory: 'task',
        severity: 'info',
        actor: user.upn,
        payloadJson: JSON.stringify({ taskId: task.taskId }),
      });
      setNotice('Task completed.');
      setTimeout(() => navigate('..'), 700);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Failed to complete task.');
    } finally {
      setBusy(false);
    }
  };

  const addNote = async (): Promise<void> => {
    if (!task || !user || note.trim().length === 0) return;
    setBusy(true);
    setNotice(null);
    try {
      await apiClient.retail.tasks.update(task.taskId, {
        notes: note.trim(),
      });
      setNote('');
      // Refetch.
      const resp = await apiClient.retail.tasks.get(task.taskId);
      if (resp.task) setTask(resp.task);
      setNotice('Note added.');
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Failed to add note.');
    } finally {
      setBusy(false);
    }
  };

  const reassign = async (newAssignee: string, reason: string): Promise<void> => {
    if (!task || !user) return;
    await apiClient.retail.tasks.reassign(task.taskId, {
      newAssigneeEmail: newAssignee,
      reason,
    });
    await apiClient.retail.events.write({
      eventType: 'task-delegated',
      eventCategory: 'task',
      severity: 'info',
      actor: user.upn,
      payloadJson: JSON.stringify({ taskId: task.taskId, newAssignee, reason }),
    });
    setNotice(`Reassigned to ${newAssignee}.`);
    // Refetch so displayed assignedTo updates.
    const resp = await apiClient.retail.tasks.get(task.taskId);
    if (resp.task) setTask(resp.task);
  };

  if (loading) {
    return (
      <div className="rtm-detail">
        <p>Loading task…</p>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="rtm-detail">
        <Banner tone="err">{error ?? 'Task unavailable.'}</Banner>
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back
        </Button>
      </div>
    );
  }

  // Minimal assignee options — real list comes from a `/v3/staff` call.
  // For Wave 2, surface self + a placeholder list so the modal works in dev.
  const assigneeOptions: ReadonlyArray<{ email: string; label: string }> = user
    ? [{ email: user.upn, label: user.displayName }]
    : [];

  return (
    <div className="rtm-detail">
      <div className="rtm-detail__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Task list
        </Button>
        <h1>{task.title}</h1>
        <Pill variant={task.severity === 'critical' ? 'err' : 'info'}>
          {task.severity}
        </Pill>
        <Pill variant="muted">{task.status}</Pill>
      </div>

      {notice && <Banner tone="ok">{notice}</Banner>}

      <WidgetCard title="Summary" stripe="tasks">
        <div className="rtm-detail__kvgrid">
          <KVPair label="Task ID" value={task.taskId} />
          <KVPair label="Type" value={task.type} />
          <KVPair label="Category" value={task.category} />
          <KVPair label="Priority" value={task.priority} />
          <KVPair label="Assigned to" value={task.assignedTo} />
          <KVPair label="Created by" value={task.createdByUser} />
          <KVPair label="Created at" value={formatLocal(task.createdAt)} />
          {task.dueDate && (
            <KVPair label="Due" value={formatLocal(task.dueDate)} />
          )}
          {task.customerName && <KVPair label="Customer" value={task.customerName} />}
          {task.cifId && <KVPair label="CIF" value={task.cifId} />}
          {task.cartId && <KVPair label="Cart" value={task.cartId} />}
        </div>
        {task.description && (
          <div style={{ marginTop: 'var(--sp-5)', color: 'var(--ink-700)', fontSize: 13 }}>
            {task.description}
          </div>
        )}
      </WidgetCard>

      <WidgetCard title="Notes" stripe="staff">
        <div className="rtm-detail__notes">
          {(Array.isArray(task.notes) ? task.notes : []).length === 0 && (
            <div className="rtm-empty">No notes yet. Add the first one below.</div>
          )}
          {(Array.isArray(task.notes) ? task.notes : []).map((n, idx) => (
            <div key={`${n.timestamp}-${idx}`} className="rtm-note">
              <div className="rtm-note__head">
                {n.author} · {formatLocal(n.timestamp)}
              </div>
              <div className="rtm-note__body">{n.text}</div>
            </div>
          ))}
          <div className="rtm-field">
            <label htmlFor="rtm-add-note">Add note</label>
            <textarea
              id="rtm-add-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Officer notes appended to task audit trail."
            />
          </div>
          <div>
            <Button
              variant="ghost"
              startIcon={<StickyNote size={14} />}
              onClick={() => void addNote()}
              disabled={busy || note.trim().length === 0}
            >
              Add note
            </Button>
          </div>
        </div>
      </WidgetCard>

      <WidgetCard title="Resolve" stripe="approvals">
        <div className="rtm-field">
          <label htmlFor="rtm-resolution">Resolution (optional)</label>
          <input
            id="rtm-resolution"
            type="text"
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            placeholder="E.g. 'Customer contacted; issue resolved.'"
          />
        </div>
        <div className="rtm-actions" style={{ marginTop: 'var(--sp-5)' }}>
          <Button
            variant="green"
            startIcon={<CheckCircle2 size={14} />}
            onClick={() => void complete()}
            disabled={busy || task.status === 'Completed'}
          >
            Complete task
          </Button>
          {canReassign && (
            <Button
              variant="primary"
              startIcon={<UserCheck size={14} />}
              onClick={() => setShowReassign(true)}
              disabled={busy}
            >
              Reassign
            </Button>
          )}
          {!canReassign && (
            <Pill variant="muted">Reassign requires Supervisor+</Pill>
          )}
        </div>
      </WidgetCard>

      {showReassign && (
        <ReassignModal
          task={task}
          assigneeOptions={assigneeOptions}
          onClose={() => setShowReassign(false)}
          onSubmit={reassign}
        />
      )}
    </div>
  );
}
